// Importar o mock service para fallback
import { mockApiService } from "./MockApiService";

// Configuração simples da URL base da API
const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:3005";

console.log("🔧 RAW_API_BASE_URL:", RAW_API_BASE_URL);

// Normalizador para evitar URLs malformadas como "http//host" e garantir protocolo
function normalizeBaseUrl(raw: string): string {
  if (!raw) return "http://localhost:3005";
  let url = String(raw).trim();
  // Corrigir casos comuns sem dois-pontos
  if (url.startsWith("http//")) url = "http://" + url.slice(6);
  if (url.startsWith("https//")) url = "https://" + url.slice(7);
  // Adicionar protocolo se ausente
  if (!/^https?:\/\//i.test(url)) {
    if (url.startsWith("//")) {
      url = "http:" + url; // torna-se http://host
    } else {
      url = "http://" + url;
    }
  }
  // Remover barras finais extras
  url = url.replace(/\/+$/, "");
  return url;
}

const API_BASE_URL = normalizeBaseUrl(RAW_API_BASE_URL);

console.log("🔧 API_BASE_URL configurada:", API_BASE_URL);

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  details?: any[];
}

class ApiService {
  private baseUrl: string;
  private useMock: boolean = false;

  constructor() {
    this.baseUrl = API_BASE_URL;
    // Permitir mock apenas se explicitamente habilitado por env
    this.useMock = process.env.REACT_APP_USE_MOCK === "true";
    // Verificação de saúde não bloqueante
    this.checkBackendHealth();
  }

  private async checkBackendHealth(): Promise<void> {
    try {
      if (process.env.NODE_ENV === "test") {
        console.log("🧪 Test mode: pulando verificação de saúde");
        return;
      }
      console.log("🔍 Verificando saúde do backend:", `${this.baseUrl}/health`);
      const controller =
        typeof AbortController !== "undefined"
          ? new AbortController()
          : (undefined as any);
      const id = controller ? setTimeout(() => controller.abort(), 3000) : null;
      const response = await fetch(`${this.baseUrl}/health`, {
        method: "GET",
        signal: controller?.signal,
      });
      if (id) clearTimeout(id as any);

      if (!response.ok) {
        // Backends podem não ter /health; apenas logar e seguir
        console.warn("⚠️ /health retornou status não OK:", response.status);
        return;
      }

      // Verificar se a resposta é JSON (API real) ou HTML (Streamlit)
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        console.log("✅ Backend API conectado:", this.baseUrl);
      } else {
        console.warn("⚠️ /health não retornou JSON. Continuando sem mock.");
      }
    } catch (error) {
      // Não alternar para mock automaticamente
      console.warn("⚠️ Erro ao verificar backend; continuando:", error);
    }
  }

  private getHeaders(includeAuth = true, useClienteToken = false): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (includeAuth) {
      // Se for requisição de cliente, usar token do cliente
      const token = useClienteToken
        ? localStorage.getItem("clienteToken")
        : localStorage.getItem("token");

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    includeAuth = true,
    useClienteToken = false,
  ): Promise<ApiResponse<T>> {
    // Não definir Content-Type se o body for FormData
    const headers = this.getHeaders(includeAuth, useClienteToken) as Record<
      string,
      string
    >;
    if (options.body instanceof FormData) {
      delete headers["Content-Type"];
    }

    // Criar AbortController para timeout de 90 segundos (triplicado)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 segundos

    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
      signal: controller.signal,
    };

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, config);
      clearTimeout(timeoutId); // Limpar timeout se a requisição completar

      // Verificar se a resposta é HTML (Streamlit) em vez de JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        throw new Error(
          "Servidor retornando HTML em vez de JSON - provavelmente Streamlit",
        );
      }

      // Tratar erro 404 especificamente
      if (response.status === 404) {
        let errorMessage = "Rota não encontrada no servidor";
        try {
          const data = await response.json();
          errorMessage = data?.message || `Rota ${endpoint} não encontrada`;
        } catch {
          // Se não conseguir parsear JSON, usar mensagem padrão
          errorMessage = `Rota ${endpoint} não encontrada (404). A funcionalidade pode ainda não estar disponível no backend.`;
        }
        throw new Error(errorMessage);
      }

      // Tratar erro 401 especificamente (credenciais inválidas)
      if (response.status === 401) {
        let errorMessage = "Email ou senha inválidos";
        try {
          const data = await response.json();
          errorMessage = data?.message || "Email ou senha inválidos";
        } catch {
          // Se não conseguir parsear JSON, usar mensagem padrão
          errorMessage =
            "Email ou senha inválidos. Verifique suas credenciais e tente novamente.";
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Erro na requisição");
      }
      return data;
    } catch (error) {
      clearTimeout(timeoutId); // Limpar timeout em caso de erro
      console.error("API Error:", error);
      throw error;
    }
  }

  // Removido: não alteramos dinamicamente a base da API

  // Métodos de Autenticação
  async login(email: string, senha: string): Promise<ApiResponse> {
    console.log(
      "🔍 Tentando login no backend:",
      `${this.baseUrl}/api/auth/login`,
    );

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");
      return mockApiService.login(email, senha);
    }

    console.log("🌐 Fazendo requisição para o backend...");
    return this.request(
      "/api/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, senha }),
      },
      false,
    );
  }

  async register(userData: {
    nome: string;
    email: string;
    senha: string;
    telefone?: string;
    cpf_cnpj?: string;
    role?: "locador" | "locatario";
  }): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.register(userData as any);
    }

    return this.request(
      "/api/auth/register",
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
      false,
    );
  }

  // Métodos de Usuário
  async getProfile(): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.getProfile();
    }

    return this.request("/api/usuarios/me");
  }

  async updateProfile(
    userData: Partial<{
      nome: string;
      telefone: string;
      cpf_cnpj: string;
    }>,
  ): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.updateProfile(userData);
    }

    return this.request("/api/usuarios/me", {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  // Métodos administrativos de usuários
  async getAllUsers(): Promise<ApiResponse> {
    return this.request("/api/usuarios");
  }

  async getUserStats(): Promise<ApiResponse> {
    return this.request("/api/usuarios/stats");
  }

  async getUserById(id: string): Promise<ApiResponse> {
    return this.request(`/api/usuarios/${id}`);
  }

  async updateUserById(id: string, userData: any): Promise<ApiResponse> {
    return this.request(`/api/usuarios/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  async deactivateUser(id: string): Promise<ApiResponse> {
    return this.request(`/api/usuarios/${id}/deactivate`, {
      method: "PATCH",
    });
  }

  async activateUser(id: string): Promise<ApiResponse> {
    return this.request(`/api/usuarios/${id}/activate`, {
      method: "PATCH",
    });
  }

  // Métodos de Imóveis
  async getImoveis(
    filters: {
      cidade?: string;
      estado?: string;
      bairro?: string;
      tipo?: string;
      tipo_imovel?: string;
      quartos?: number;
      quartos_min?: number;
      quartos_max?: number;
      banheiros?: number;
      banheiros_min?: number;
      banheiros_max?: number;
      vagas_garagem?: number;
      vagas_min?: number;
      vagas_max?: number;
      area_min?: number;
      area_max?: number;
      valor_min?: number;
      valor_max?: number;
      valorMaximo?: string | number;
      valor_venda_min?: number;
      valor_venda_max?: number;
      apenas_aluguel?: boolean;
      apenas_venda?: boolean;
      aluguel_e_venda?: boolean;
      aceita_pet?: boolean;
      mobiliado?: boolean;
      ar_condicionado?: boolean;
      internet?: boolean;
      comodidades?: string[];
      search?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.getImoveis(filters);
    }

    const queryParams = new URLSearchParams();

    // Localização
    if (filters.cidade) queryParams.append("cidade", filters.cidade);
    if (filters.estado) queryParams.append("estado", filters.estado);
    if (filters.bairro) queryParams.append("bairro", filters.bairro);

    // Tipo de imóvel
    if (filters.tipo_imovel) {
      queryParams.append("tipo_imovel", filters.tipo_imovel);
    } else if (filters.tipo) {
      queryParams.append("tipo_imovel", filters.tipo);
    }

    // Quartos
    if (filters.quartos_min !== undefined)
      queryParams.append("quartos_min", filters.quartos_min.toString());
    if (filters.quartos_max !== undefined)
      queryParams.append("quartos_max", filters.quartos_max.toString());
    if (
      filters.quartos !== undefined &&
      filters.quartos_min === undefined &&
      filters.quartos_max === undefined
    ) {
      queryParams.append("quartos_min", filters.quartos.toString());
    }

    // Banheiros
    if (filters.banheiros_min !== undefined)
      queryParams.append("banheiros_min", filters.banheiros_min.toString());
    if (filters.banheiros_max !== undefined)
      queryParams.append("banheiros_max", filters.banheiros_max.toString());
    if (
      filters.banheiros !== undefined &&
      filters.banheiros_min === undefined &&
      filters.banheiros_max === undefined
    ) {
      queryParams.append("banheiros_min", filters.banheiros.toString());
    }

    // Vagas
    if (filters.vagas_min !== undefined)
      queryParams.append("vagas_min", filters.vagas_min.toString());
    if (filters.vagas_max !== undefined)
      queryParams.append("vagas_max", filters.vagas_max.toString());
    if (
      filters.vagas_garagem !== undefined &&
      filters.vagas_min === undefined &&
      filters.vagas_max === undefined
    ) {
      queryParams.append("vagas_min", filters.vagas_garagem.toString());
    }

    // Área
    if (filters.area_min !== undefined)
      queryParams.append("area_min", filters.area_min.toString());
    if (filters.area_max !== undefined)
      queryParams.append("area_max", filters.area_max.toString());

    // Valores de aluguel
    if (filters.valor_min !== undefined)
      queryParams.append("valor_min", filters.valor_min.toString());
    if (filters.valor_max !== undefined) {
      queryParams.append("valor_max", filters.valor_max.toString());
    } else if (filters.valorMaximo) {
      const valorMax =
        typeof filters.valorMaximo === "string"
          ? parseFloat(filters.valorMaximo)
          : filters.valorMaximo;
      if (!isNaN(valorMax) && valorMax > 0) {
        queryParams.append("valor_max", valorMax.toString());
      }
    }

    // Valores de venda
    if (filters.valor_venda_min !== undefined)
      queryParams.append("valor_venda_min", filters.valor_venda_min.toString());
    if (filters.valor_venda_max !== undefined)
      queryParams.append("valor_venda_max", filters.valor_venda_max.toString());

    // Filtros de finalidade - apenas enviar se explicitamente definidos como true
    // Não enviar se for false ou undefined para que o backend retorne todos os imóveis
    if (filters.apenas_aluguel === true)
      queryParams.append("apenas_aluguel", "true");
    if (filters.apenas_venda === true)
      queryParams.append("apenas_venda", "true");
    if (filters.aluguel_e_venda === true)
      queryParams.append("aluguel_e_venda", "true");

    // Características
    if (filters.aceita_pet !== undefined)
      queryParams.append("aceita_pet", filters.aceita_pet.toString());
    if (filters.mobiliado !== undefined)
      queryParams.append("mobiliado", filters.mobiliado.toString());
    if (filters.ar_condicionado !== undefined)
      queryParams.append("ar_condicionado", filters.ar_condicionado.toString());
    if (filters.internet !== undefined)
      queryParams.append("internet", filters.internet.toString());

    // Busca por texto
    if (filters.search) queryParams.append("search", filters.search);

    // Paginação
    if (filters.page !== undefined)
      queryParams.append("page", filters.page.toString());
    if (filters.limit !== undefined)
      queryParams.append("limit", filters.limit.toString());

    // Status padrão para busca pública (apenas imóveis disponíveis)
    queryParams.append("status", "disponivel");

    const endpoint = queryParams.toString()
      ? `/api/imoveis?${queryParams.toString()}`
      : "/api/imoveis";

    console.log("🏠 Buscando imóveis:", `${this.baseUrl}${endpoint}`);
    console.log("🔍 Filtros de finalidade enviados:", {
      apenas_aluguel: filters.apenas_aluguel,
      apenas_venda: filters.apenas_venda,
      aluguel_e_venda: filters.aluguel_e_venda,
    });
    return this.request(endpoint, {}, false);
  }

  async getImovel(id: string): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.getImovel(id);
    }

    console.log("🏠 Buscando imóvel:", `${this.baseUrl}/imoveis/${id}`);
    return this.request(`/api/imoveis/${id}`, {}, false);
  }

  async registrarVisualizacao(codigo: number): Promise<ApiResponse> {
    try {
      // Validação do código conforme documentação
      if (!codigo || codigo <= 0 || !Number.isInteger(codigo)) {
        console.warn("⚠️ Código inválido para registrar visualização:", codigo);
        return {
          success: false,
          message: "Código inválido. Deve ser um número positivo.",
        };
      }

      console.log(
        "👁️ Registrando visualização do imóvel:",
        `${this.baseUrl}/api/imoveis/${codigo}/visualizar`,
      );
      return await this.request(
        `/api/imoveis/${codigo}/visualizar`,
        {
          method: "POST",
        },
        false,
      );
    } catch (error) {
      // Tratar erros silenciosamente para não impactar a experiência do usuário
      console.warn("⚠️ Erro ao registrar visualização (não crítico):", error);
      return {
        success: false,
        message: "Erro ao registrar visualização",
        error: error instanceof Error ? error.message : "Erro desconhecido",
      };
    }
  }

  async getImovelByCodigo(codigo: number): Promise<any> {
    if (this.useMock) {
      // No mock, vamos simular busca por código
      const mockImoveis = await mockApiService.getImoveis();
      const imovel = mockImoveis.data?.find(
        (imovel: any) => imovel.codigo === codigo,
      );
      if (imovel) {
        return imovel;
      }
      throw new Error("Imóvel não encontrado");
    }

    // Validação do código conforme documentação
    if (!codigo || codigo <= 0 || !Number.isInteger(codigo)) {
      throw new Error("Código inválido. Deve ser um número positivo.");
    }

    // Endpoint correto conforme documentação: GET /api/imoveis?codigo={numero}
    const endpoint = `/api/imoveis?codigo=${codigo}`;
    console.log("🏠 Buscando imóvel por código:", `${this.baseUrl}${endpoint}`);
    const response = await this.request(endpoint, {}, false);

    console.log("📦 Resposta recebida:", JSON.stringify(response, null, 2));

    // Tratamento conforme estrutura da resposta da documentação:
    // { success: true, data: { imoveis: [...], pagination: {...} } }
    if (response.success && response.data) {
      const data = response.data as any;

      // Verificar se data tem a estrutura esperada com imoveis array
      if (data.imoveis && Array.isArray(data.imoveis)) {
        if (data.imoveis.length > 0) {
          const imovel = data.imoveis[0];
          console.log(
            `✅ Imóvel encontrado: ${imovel.titulo} (Status: ${imovel.status})`,
          );
          return imovel;
        }
      }

      // Fallback para estrutura alternativa (caso data seja diretamente o array)
      if (Array.isArray(data) && data.length > 0) {
        return data[0];
      }

      // Fallback para estrutura com objeto direto
      if (!Array.isArray(data) && data && !data.imoveis) {
        return data;
      }
    }

    console.error(
      "❌ Estrutura de resposta inesperada ou imóvel não encontrado:",
      response,
    );
    throw new Error("Imóvel não encontrado");
  }

  async createImovel(imovelData: any): Promise<ApiResponse> {
    return this.request("/api/imoveis", {
      method: "POST",
      body: JSON.stringify(imovelData),
    });
  }

  async updateImovel(id: string, imovelData: any): Promise<ApiResponse> {
    return this.request(`/api/imoveis/${id}`, {
      method: "PUT",
      body: JSON.stringify(imovelData),
    });
  }

  async deleteImovel(id: string): Promise<ApiResponse> {
    return this.request(`/api/imoveis/${id}`, {
      method: "DELETE",
    });
  }

  async getMyImoveis(): Promise<ApiResponse> {
    // Endpoint específico para os imóveis do usuário logado
    return this.request("/api/imoveis/meus");
  }

  // Métodos administrativos de imóveis
  async getAllImoveis(): Promise<ApiResponse> {
    return this.request("/api/imoveis/admin/todos");
  }

  async getImoveisStats(): Promise<ApiResponse> {
    return this.request("/api/imoveis/admin/stats");
  }

  async updateImovelStatus(
    id: string,
    status: string,
    motivoRejeicao?: string,
  ): Promise<ApiResponse> {
    const body: any = { status };
    if (motivoRejeicao) {
      body.motivo_rejeicao = motivoRejeicao;
    }

    return this.request(`/api/imoveis/admin/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  // Métodos de Mídias
  async uploadMidias(imovelId: string, files: File[]): Promise<ApiResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("midias", file);
    });

    return this.request(`/api/midias/upload/${imovelId}`, {
      method: "POST",
      body: formData,
    });
  }

  async getMidias(imovelId: string): Promise<ApiResponse> {
    return this.request(`/api/midias/imovel/${imovelId}`, {}, false);
  }

  async updateMidia(
    id: string,
    data: {
      titulo?: string;
      descricao?: string;
      ordem?: number;
      ativa?: boolean;
      is_capa?: boolean;
    },
  ): Promise<ApiResponse> {
    return this.request(`/api/midias/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Método simples para construir URLs de imagens
  async getImageProxy(imagePath: string): Promise<string> {
    if (!imagePath) {
      return "/placeholder-property.jpg";
    }

    // Se já é uma URL completa, extrair somente o caminho a partir de /uploads
    if (imagePath.startsWith("http")) {
      try {
        const u = new URL(imagePath);
        const p = u.pathname || "";
        const idx = p.toLowerCase().indexOf("/uploads");
        if (idx >= 0) {
          imagePath = p.substring(idx);
        } else {
          return imagePath; // não é um caminho de uploads
        }
      } catch {
        return imagePath;
      }
    }

    // Construir URL baseada na API_BASE_URL
    const baseUrl = API_BASE_URL.replace(/\/+$/, "");
    const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

    // Construir URL no mesmo host da API
    try {
      const api = new URL(baseUrl);
      return `${api.origin}${cleanPath}`; // sem duplicar /api
    } catch {
      return `${baseUrl}${cleanPath}`;
    }
  }

  async deleteMidia(id: string): Promise<ApiResponse> {
    return this.request(`/api/midias/${id}`, {
      method: "DELETE",
    });
  }

  async reorderMidias(
    imovelId: string,
    midiaIds: string[],
  ): Promise<ApiResponse> {
    return this.request(`/api/midias/reorder/${imovelId}`, {
      method: "PATCH",
      body: JSON.stringify({ midia_ids: midiaIds }),
    });
  }

  // Métodos adicionais de mídias
  async getMidiaById(id: string): Promise<ApiResponse> {
    return this.request(`/api/midias/${id}`, {}, false);
  }

  async getMidiasStats(): Promise<ApiResponse> {
    return this.request("/api/midias/admin/stats");
  }

  // Métodos Administrativos
  async getDashboard(): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Buscando dashboard admin:",
      `${this.baseUrl}/api/admin/dashboard`,
    );
    return this.request("/api/admin/dashboard");
  }

  async getImoveisPendentes(): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Buscando imóveis pendentes:",
      `${this.baseUrl}/api/admin/imoveis/pendentes`,
    );
    const response = await this.request("/api/admin/imoveis/pendentes");

    // Buscar mídias individualmente para TODOS os imóveis em paralelo
    if (response.success && response.data && Array.isArray(response.data)) {
      // Criar array de promises para buscar mídias em paralelo
      const midiasPromises = response.data.map(async (imovel: any) => {
        try {
          const individualResponse = await this.request(
            `/api/imoveis/${imovel.id}`,
          );

          if (
            individualResponse.success &&
            individualResponse.data &&
            (individualResponse.data as any).midias
          ) {
            imovel.midias = (individualResponse.data as any).midias;
          } else {
            imovel.midias = [];
          }
        } catch (error) {
          imovel.midias = [];
        }
      });

      // Aguardar todas as buscas de mídias terminarem
      await Promise.all(midiasPromises);
    }

    return response;
  }

  async getImoveisAprovados(): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Buscando imóveis aprovados:",
      `${this.baseUrl}/api/admin/imoveis/aprovados`,
    );
    const response = await this.request("/api/admin/imoveis/aprovados");

    // Buscar mídias individualmente para TODOS os imóveis em paralelo
    if (response.success && response.data && Array.isArray(response.data)) {
      const midiasPromises = response.data.map(async (imovel: any) => {
        try {
          const individualResponse = await this.request(
            `/api/imoveis/${imovel.id}`,
          );

          if (
            individualResponse.success &&
            individualResponse.data &&
            (individualResponse.data as any).midias
          ) {
            imovel.midias = (individualResponse.data as any).midias;
          } else {
            imovel.midias = [];
          }
        } catch (error) {
          imovel.midias = [];
        }
      });

      await Promise.all(midiasPromises);
    }

    return response;
  }

  async getImoveisRejeitados(): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Buscando imóveis rejeitados:",
      `${this.baseUrl}/api/admin/imoveis/rejeitados`,
    );
    const response = await this.request("/api/admin/imoveis/rejeitados");

    // Buscar mídias individualmente para TODOS os imóveis em paralelo
    if (response.success && response.data && Array.isArray(response.data)) {
      const midiasPromises = response.data.map(async (imovel: any) => {
        try {
          const individualResponse = await this.request(
            `/api/imoveis/${imovel.id}`,
          );

          if (
            individualResponse.success &&
            individualResponse.data &&
            (individualResponse.data as any).midias
          ) {
            imovel.midias = (individualResponse.data as any).midias;
          } else {
            imovel.midias = [];
          }
        } catch (error) {
          imovel.midias = [];
        }
      });

      await Promise.all(midiasPromises);
    }

    return response;
  }

  async getImoveisAlugados(): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Buscando imóveis alugados:",
      `${this.baseUrl}/api/admin/imoveis/alugados`,
    );
    const response = await this.request("/api/admin/imoveis/alugados");

    // Buscar mídias individualmente para TODOS os imóveis em paralelo
    if (response.success && response.data && Array.isArray(response.data)) {
      const midiasPromises = response.data.map(async (imovel: any) => {
        try {
          const individualResponse = await this.request(
            `/api/imoveis/${imovel.id}`,
          );

          if (
            individualResponse.success &&
            individualResponse.data &&
            (individualResponse.data as any).midias
          ) {
            imovel.midias = (individualResponse.data as any).midias;
          } else {
            imovel.midias = [];
          }
        } catch (error) {
          imovel.midias = [];
        }
      });

      await Promise.all(midiasPromises);
    }

    return response;
  }

  async getAdminImoveisByStatus(status: string): Promise<ApiResponse> {
    console.log("👨‍💼 Buscando admin/imoveis por status:", status);
    const endpoint = `/api/admin/imoveis?status=${encodeURIComponent(status)}`;
    const response = await this.request(endpoint);

    // Buscar mídias individualmente para TODOS os imóveis em paralelo
    if (response.success && response.data && Array.isArray(response.data)) {
      console.log(
        `🔍 Buscando mídias individualmente para imóveis com status: ${status}`,
      );

      const midiasPromises = response.data.map(async (imovel: any) => {
        try {
          const individualResponse = await this.request(
            `/api/imoveis/${imovel.id}`,
          );

          if (
            individualResponse.success &&
            individualResponse.data &&
            (individualResponse.data as any).midias
          ) {
            imovel.midias = (individualResponse.data as any).midias;

            // Log detalhado das mídias retornadas pelo backend
            const midias = (individualResponse.data as any).midias;
            console.log(
              `📸 BACKEND - Mídias do imóvel "${imovel.titulo}" (${imovel.id}):`,
              {
                total: midias.length,
                midias: midias.map((m: any) => ({
                  id: m.id,
                  arquivo: m.url.substring(m.url.lastIndexOf("/") + 1),
                  ordem: m.ordem,
                  is_capa: m.is_capa,
                  ativa: m.ativa,
                })),
              },
            );
          } else {
            imovel.midias = [];
          }
        } catch (error) {
          console.log(
            `❌ Erro ao buscar mídias do imóvel ${status} ${imovel.id}:`,
            error,
          );
          imovel.midias = [];
        }
      });

      await Promise.all(midiasPromises);
      console.log(`✅ Todas as mídias de imóveis ${status} foram carregadas!`);
    }

    return response;
  }

  private normalizeListFromResponse(resp: ApiResponse): any[] {
    if (!resp) return [];
    const d: any = (resp as any).data ?? resp;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.imoveis)) return d.imoveis;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d?.results)) return d.results;
    return [];
  }

  async adminListWithParams(
    params: Record<string, string>,
  ): Promise<{ list: any[]; raw: ApiResponse | null }> {
    const qs = new URLSearchParams(params).toString();
    const endpoint = `/api/admin/imoveis${qs ? `?${qs}` : ""}`;
    const resp = await this.request(endpoint);
    const list = this.normalizeListFromResponse(resp);
    return { list, raw: resp };
  }

  async tryAdminStatuses(
    possibleParams: Array<Record<string, string>>,
  ): Promise<{ list: any[]; used: Record<string, string> | null }> {
    for (const p of possibleParams) {
      try {
        const { list } = await this.adminListWithParams(p);
        if (Array.isArray(list) && list.length > 0) {
          console.log("✅ Encontrado com parâmetros:", p, "qtde:", list.length);
          return { list, used: p };
        }
      } catch (e) {
        console.warn("⚠️ Falha com parâmetros", p, e);
      }
    }
    return { list: [], used: null };
  }

  async approveImovel(id: string): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Aprovando imóvel:",
      `${this.baseUrl}/api/admin/imoveis/${id}/approve`,
    );
    return this.request(`/api/admin/imoveis/${id}/approve`, {
      method: "PATCH",
    });
  }

  async rejectImovel(id: string, motivo: string): Promise<ApiResponse> {
    console.log(
      "👨‍💼 Rejeitando imóvel:",
      `${this.baseUrl}/api/admin/imoveis/${id}/reject`,
    );
    return this.request(`/api/admin/imoveis/${id}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ motivo }),
    });
  }

  async pausarImovel(id: string): Promise<ApiResponse> {
    console.log(
      "⏸️ Pausando imóvel:",
      `${this.baseUrl}/api/imoveis/admin/${id}/status`,
    );
    return this.request(`/api/imoveis/admin/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status: "pausado" }),
    });
  }

  async marcarComoVendido(id: string): Promise<ApiResponse> {
    console.log(
      "💰 Marcando imóvel como vendido:",
      `${this.baseUrl}/api/imoveis/admin/${id}/status`,
    );
    return this.request(`/api/imoveis/admin/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status: "vendido" }),
    });
  }

  async promoteUser(id: string): Promise<ApiResponse> {
    return this.request(`/api/admin/usuarios/${id}/promote`, {
      method: "PATCH",
    });
  }

  async demoteUser(id: string): Promise<ApiResponse> {
    return this.request(`/api/admin/usuarios/${id}/demote`, {
      method: "PATCH",
    });
  }

  // Métodos administrativos adicionais
  async getAdminActivity(): Promise<ApiResponse> {
    return this.request("/api/admin/activity");
  }

  async getAdminRelatorios(
    tipo: string,
    periodo?: string,
    formato?: string,
  ): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (periodo) queryParams.append("periodo", periodo);
    if (formato) queryParams.append("formato", formato);

    const queryString = queryParams.toString();
    const endpoint = `/api/admin/relatorios/${tipo}${queryString ? `?${queryString}` : ""}`;

    return this.request(endpoint);
  }

  async getAdminConfig(): Promise<ApiResponse> {
    return this.request("/api/admin/config");
  }

  async updateAdminConfig(configData: any): Promise<ApiResponse> {
    return this.request("/admin/config", {
      method: "PUT",
      body: JSON.stringify(configData),
    });
  }

  // Métodos de Histórico
  async getImovelHistorico(imovelId: string): Promise<ApiResponse> {
    return this.request(`/api/historico/imovel/${imovelId}`);
  }

  async getImovelUltimaAlteracao(imovelId: string): Promise<ApiResponse> {
    return this.request(`/api/historico/imovel/${imovelId}/ultima`);
  }

  async getHistoricoPendentes(): Promise<ApiResponse> {
    return this.request("/api/historico/pendentes");
  }

  // Métodos de utilidade
  async checkHealth(): Promise<ApiResponse> {
    if (this.useMock) {
      return mockApiService.checkHealth();
    }

    return this.request("/api/health", {}, false);
  }

  async getInfo(): Promise<ApiResponse> {
    return this.request("/api/info", {}, false);
  }

  /**
   * Envia formulário de contato
   * @param contactData - Dados do formulário de contato
   * @returns Resposta da API
   */
  async sendContact(contactData: {
    nome: string;
    email: string;
    telefone?: string;
    tipoContato: string;
    mensagem: string;
  }): Promise<ApiResponse> {
    console.log("📧 Enviando contato:", contactData);

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");
      // Simular sucesso no mock
      return Promise.resolve({
        success: true,
        message: "Mensagem enviada com sucesso (mock)",
      });
    }

    return this.request(
      "/api/contact",
      {
        method: "POST",
        body: JSON.stringify(contactData),
      },
      false,
    );
  }

  /**
   * Cadastra um novo cliente
   * @param clienteData - Dados do cliente
   * @returns Resposta da API com dados do cliente cadastrado
   */
  async cadastrarCliente(clienteData: {
    nome: string;
    email: string;
    telefone: string;
    senha: string;
  }): Promise<ApiResponse> {
    console.log("👤 Cadastrando cliente:", clienteData);

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");
      const mockClienteId = "mock-cliente-" + Date.now();
      return Promise.resolve({
        success: true,
        message: "Cliente cadastrado com sucesso (mock)",
        data: {
          cliente: {
            id: mockClienteId,
            nome: clienteData.nome,
            email: clienteData.email,
            telefone: clienteData.telefone,
            created_at: new Date().toISOString(),
          },
          token: "mock-token-" + Date.now(),
        },
      });
    }

    return this.request(
      "/api/clientes",
      {
        method: "POST",
        body: JSON.stringify(clienteData),
      },
      false,
    );
  }

  /**
   * Faz login de um cliente
   * @param credentials - Credenciais de login (email e senha)
   */
  async loginCliente(credentials: {
    email: string;
    senha: string;
  }): Promise<ApiResponse> {
    console.log("🔐 Fazendo login de cliente:", { email: credentials.email });

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");
      const mockClienteId = "mock-cliente-" + Date.now();
      return Promise.resolve({
        success: true,
        message: "Login realizado com sucesso (mock)",
        data: {
          cliente: {
            id: mockClienteId,
            nome: "Cliente Mock",
            email: credentials.email,
            telefone: "(00) 00000-0000",
            created_at: new Date().toISOString(),
          },
          token: "mock-token-" + Date.now(),
        },
      });
    }

    return this.request(
      "/api/clientes/login",
      {
        method: "POST",
        body: JSON.stringify(credentials),
      },
      false,
    );
  }

  /**
   * Adiciona um imóvel aos favoritos do cliente
   * @param clienteId - ID do cliente
   * @param imovelId - ID do imóvel
   * @returns Resposta da API
   */
  async adicionarFavorito(
    clienteId: string,
    imovelId: string,
  ): Promise<ApiResponse> {
    console.log("❤️ Adicionando favorito:", { clienteId, imovelId });

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");

      // Mock usando localStorage para persistir favoritos
      const mockFavoritosKey = `mock_favoritos_${clienteId}`;
      const favoritosStr = localStorage.getItem(mockFavoritosKey);
      const favoritos: any[] = favoritosStr ? JSON.parse(favoritosStr) : [];

      // Verificar se já existe
      if (
        favoritos.some(
          (f: any) => f.imovel?.id === imovelId || f.imovel_id === imovelId,
        )
      ) {
        return Promise.resolve({
          success: false,
          message: "Imóvel já está nos favoritos (mock)",
        });
      }

      // Tentar buscar dados do imóvel antes de adicionar
      let imovelData: any = { id: imovelId };
      try {
        const imovelResponse = await this.getImovel(imovelId);
        if (imovelResponse.success && imovelResponse.data) {
          imovelData = imovelResponse.data;
        }
      } catch (e) {
        console.warn(
          "Não foi possível buscar dados do imóvel ao adicionar favorito:",
          e,
        );
      }

      // Adicionar novo favorito
      const novoFavorito = {
        favorito_id: "mock-favorito-" + Date.now(),
        adicionado_em: new Date().toISOString(),
        imovel: imovelData,
        imovel_id: imovelId,
      };

      favoritos.push(novoFavorito);
      localStorage.setItem(mockFavoritosKey, JSON.stringify(favoritos));

      console.log("✅ Favorito adicionado (mock):", {
        clienteId,
        imovelId,
        totalFavoritos: favoritos.length,
      });

      // Invalidar cache de favoritos
      localStorage.removeItem("favoritosCache");
      localStorage.removeItem("favoritosTimestamp");

      return Promise.resolve({
        success: true,
        message: "Imóvel adicionado aos favoritos com sucesso (mock)",
        data: {
          id: novoFavorito.favorito_id,
          cliente_id: clienteId,
          imovel_id: imovelId,
          created_at: novoFavorito.adicionado_em,
        },
      });
    }

    return this.request(
      `/api/clientes/${clienteId}/favoritos`,
      {
        method: "POST",
        body: JSON.stringify({ imovelId }),
      },
      true,
      true,
    ); // Usar autenticação com token do cliente
  }

  /**
   * Lista os favoritos do cliente
   * @param clienteId - ID do cliente
   * @returns Resposta da API com lista de favoritos
   */
  async listarFavoritos(clienteId: string): Promise<ApiResponse> {
    console.log("📋 Listando favoritos do cliente:", clienteId);

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");

      // Mock usando localStorage para persistir favoritos
      const mockFavoritosKey = `mock_favoritos_${clienteId}`;
      const favoritosStr = localStorage.getItem(mockFavoritosKey);
      const favoritos: any[] = favoritosStr ? JSON.parse(favoritosStr) : [];

      // Buscar dados dos imóveis favoritados
      // Tentar buscar dados completos dos imóveis da API
      const favoritosCompletos = await Promise.all(
        favoritos.map(async (f: any) => {
          const imovelId = f.imovel_id || f.imovel?.id;

          // Tentar buscar dados do imóvel da API real (se não estiver em modo mock)
          try {
            // Se não estiver em modo mock, tentar buscar da API
            if (!this.useMock) {
              try {
                const imovelResponse = await this.getImovel(imovelId);
                if (imovelResponse.success && imovelResponse.data) {
                  return {
                    ...f,
                    imovel: imovelResponse.data,
                  };
                }
              } catch (e) {
                console.warn(
                  "Não foi possível buscar dados completos do imóvel:",
                  e,
                );
              }
            }

            // Se não conseguir buscar, usar estrutura mínima
            return {
              ...f,
              imovel: f.imovel || {
                id: imovelId,
                titulo: "Imóvel favoritado",
                codigo: imovelId?.substring(0, 8) || "N/A",
              },
            };
          } catch (e) {
            console.error("Erro ao buscar imóvel:", e);
            // Se não encontrar, retornar com estrutura mínima
            return {
              ...f,
              imovel: f.imovel || {
                id: imovelId,
                titulo: "Imóvel favoritado",
                codigo: imovelId?.substring(0, 8) || "N/A",
              },
            };
          }
        }),
      );

      return Promise.resolve({
        success: true,
        message: `${favoritosCompletos.length} imóvel(is) favorito(s) encontrado(s) (mock)`,
        data: {
          total: favoritosCompletos.length,
          favoritos: favoritosCompletos,
        },
      });
    }

    return this.request(
      `/api/clientes/${clienteId}/favoritos`,
      {
        method: "GET",
      },
      true,
      true,
    ); // Usar autenticação com token do cliente
  }

  /**
   * Remove um imóvel dos favoritos do cliente
   * @param clienteId - ID do cliente
   * @param imovelId - ID do imóvel
   * @returns Resposta da API
   */
  async removerFavorito(
    clienteId: string,
    imovelId: string,
  ): Promise<ApiResponse> {
    console.log("💔 Removendo favorito:", { clienteId, imovelId });

    if (this.useMock) {
      console.log("⚠️ Usando Mock API (backend não disponível)");

      // Mock usando localStorage para persistir favoritos
      const mockFavoritosKey = `mock_favoritos_${clienteId}`;
      const favoritosStr = localStorage.getItem(mockFavoritosKey);
      const favoritos: any[] = favoritosStr ? JSON.parse(favoritosStr) : [];

      // Remover favorito
      const favoritosAtualizados = favoritos.filter(
        (f: any) => f.imovel?.id !== imovelId && f.imovel_id !== imovelId,
      );

      if (favoritosAtualizados.length === favoritos.length) {
        return Promise.resolve({
          success: false,
          message: "Favorito não encontrado (mock)",
        });
      }

      localStorage.setItem(
        mockFavoritosKey,
        JSON.stringify(favoritosAtualizados),
      );

      // Invalidar cache de favoritos
      localStorage.removeItem("favoritosCache");
      localStorage.removeItem("favoritosTimestamp");

      return Promise.resolve({
        success: true,
        message: "Imóvel removido dos favoritos com sucesso (mock)",
      });
    }

    return this.request(
      `/api/clientes/${clienteId}/favoritos/${imovelId}`,
      {
        method: "DELETE",
      },
      true,
      true,
    ); // Usar autenticação com token do cliente
  }
}

export const apiService = new ApiService();
export default apiService;
